import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const getEventTypeLabel = (event) => {
  // Use event_type_display if available (from backend), otherwise use default labels
  if (event.event_type_display) {
    return event.event_type_display;
  }
  const EVENT_TYPE_LABELS = {
    assembly: 'اجتماع',
    holiday: 'عطلة',
    exam_period: 'فترة امتحانات',
    trip: 'رحلة',
    celebration: 'احتفال',
    meeting: 'اجتماع',
    workshop: 'ورشة عمل',
    sports_day: 'يوم رياضي',
    graduation: 'تخرج',
    other: 'أخرى',
  };
  return EVENT_TYPE_LABELS[event.event_type] || event.event_type || '-';
};

const EventList = ({
  events = [],
  loading = false,
  onView,
  onEdit,
  onDelete,
  onPublish,
  onCancel,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          لا توجد فعاليات
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>العنوان</TableCell>
            <TableCell>النوع</TableCell>
            <TableCell>التاريخ</TableCell>
            <TableCell>الوقت</TableCell>
            <TableCell>المكان</TableCell>
            <TableCell>الحالة</TableCell>
            <TableCell>الإجراءات</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {event.title}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getEventTypeLabel(event)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(event.start_date)}
                  {event.end_date && event.end_date !== event.start_date && (
                    <span> - {formatDate(event.end_date)}</span>
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {event.start_time && formatTime(event.start_time)}
                  {event.end_time && ` - ${formatTime(event.end_time)}`}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {event.location || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack useFlexGap direction="row" spacing={1}>
                  {event.is_cancelled ? (
                    <Chip label="ملغي" color="error" size="small" />
                  ) : event.is_published ? (
                    <Chip label="منشور" color="success" size="small" />
                  ) : (
                    <Chip label="غير منشور" color="default" size="small" />
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <Stack useFlexGap direction="row" spacing={1}>
                  <Tooltip title="عرض">
                    <IconButton size="small" onClick={() => onView(event.id)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="تعديل">
                    <IconButton size="small" onClick={() => onEdit(event.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!event.is_cancelled && (
                    <Tooltip title={event.is_published ? 'إلغاء النشر' : 'نشر'}>
                      <IconButton
                        size="small"
                        onClick={() => onPublish(event.id, !event.is_published)}
                      >
                        {event.is_published ? (
                          <UnpublishIcon fontSize="small" />
                        ) : (
                          <PublishIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  {!event.is_cancelled && (
                    <Tooltip title="إلغاء">
                      <IconButton size="small" onClick={() => onCancel(event.id)}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="حذف">
                    <IconButton size="small" onClick={() => onDelete(event.id)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EventList;
